// ============================================================
// TERROR TCG — THE GRIMOIRE OF TERROR
// Complete Interactive Rulebook — v4 · Gilded Obsidian Edition
// ------------------------------------------------------------
// v3 → v4:
//   • Local manuscript palette DELETED — imports GM + material
//     layer from theme.js (Gilded Obsidian v4, COMPAT LAW).
//   • Book UX: page-turn footer on every chapter, ←/→ keyboard
//     page turning, gold reading-progress bar, folio counter.
//   • The book remembers you — last chapter + visited chapters
//     persist in localStorage; reopening resumes where you left.
//   • Faceted chips/buttons, metal-strip header, obsidian slabs
//     (Material Laws 1–7). Keyframes injected imperatively.
//   • Narrative prose reads in GM.fontBody (Cormorant voice);
//     chrome, labels and reference tables stay Cinzel.
//   • Content re-verified against Rulez_v2 (July 4, 2026 canon).
// July 25, 2026 wave (Resurrection & Primera):
//   • Resurrection tallies by line — Beb 3× · Champion 2× ·
//     Lord 1× per Solrend, per card instance; EVERY method counts.
//   • NEW Chapter 9 — Primeras (The Stamps): innate Ilk/School
//     powers on every Escendant. Chapters 9–21 renumbered 10–22.
//   • Rulings 16–17 added; Glossary + Quick Reference updated.
// ============================================================
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
// T15 — the search well reads the book the same way the reader does:
// each chapter rendered to static markup once, stripped to text, cached.
// The index can never drift from the pages because it IS the pages.
import {
  GM,
  GOLD_METAL_H, SILVER_METAL_H,
  OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT, OBSIDIAN_RECESS,
  facetTB, SHADOW_RAISED, SHADOW_RECESS,
  bloom, textGlow, TEXT_SET,
} from "./theme";
// R101 — the Deviant fare is PRINTED from rules.DEVIANT_FARE, never
// retyped. The two tables below were a fourth and fifth hand-kept copy
// and both had the L3 / 0-trait Anima leg wrong (5, not 4).
// ⚠ `DEVIANT_FARE` NO LONGER BELONGS HERE. The two fare tables that
// printed it were hand-written chapters and are gone — the book prints
// its own, out of `RULEZ_V2.md`, which is where R101's numbers were
// always supposed to be read from.
import { RULEZ_SECTIONS, RULEZ_PREAMBLE, RulezChapter, RulezMarkdown, rulezText, rulezNormalize } from "./RulezRender";
import { UnstableWorldBG, OrnateDivider } from "./TerrorUI";
import { useModalKeys } from "./KeyboardNav";
import { useStageRect } from "./RoomBackdrop"; // C5 — the one canonical room rectangle

const JX = GM.amethystBright; // J'ynxi's signature purple — amethyst starlight

// ── Keyframes — imperative injection (the keyframe-trap-safe
//    pattern). Never <style> blocks inside components. ─────────
function ensureRbKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("terror-rulebook-keyframes")) return;
  const el = document.createElement("style");
  el.id = "terror-rulebook-keyframes";
  el.textContent = `
    @keyframes rbPageIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes rbGemPls { 0%,100% { opacity: .55; } 50% { opacity: 1; } }
  `;
  document.head.appendChild(el);
}
ensureRbKeyframes();

// ── The book remembers you — localStorage persistence ────────
// v5 (Impera rename): the chapter id "primera" became "impera", so a
// v4 bookmark could point `last` at a chapter that no longer exists.
// Bumping the key discards stale bookmarks instead of resolving them.
// ⚠ BUMPED ON 2026-09-13, AND THE BUMP IS NOT COSMETIC. The chapter ids
// are derived from `RULEZ_V2.md`'s headings now, so every id a returning
// reader has stored under the old key names a chapter that no longer
// exists — a progress rail of thirty dots, none of which light. A new
// key retires that state cleanly instead of half-matching it.
const LS_KEY = "terror_rulebook_v6";
function loadRB() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { last: null, seen: [] };
    const p = JSON.parse(raw);
    return {
      last: typeof p.last === "string" ? p.last : null,
      seen: Array.isArray(p.seen) ? p.seen : [],
    };
  } catch { return { last: null, seen: [] }; }
}
function saveRB(last, seenSet) {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ last, seen: [...seenSet] })); } catch {}
}

// ── Tooltip term ─────────────────────────────────────────────

// ── Narrative prose — the Cormorant reading voice ────────────

// ── Expandable section — faceted obsidian slab ───────────────

// ── Info boxes ───────────────────────────────────────────────

// ── J'ynxi narration block — amethyst voice, Cormorant italic ─
function Jynxi({ children, sign = false }) {
  return (
    <div style={{
      margin: "16px 0",
      padding: "14px 18px 14px 22px",
      borderLeft: `2px solid ${JX}88`,
      background: `linear-gradient(90deg, ${GM.amethyst}12, transparent 80%)`,
      borderRadius: "0 6px 6px 0",
      fontStyle: "italic",
      color: "#cbb8ee",
      fontSize: 15,
      lineHeight: 1.9,
      fontFamily: GM.fontBody,
      textShadow: TEXT_SET,
      position: "relative",
      boxShadow: `inset 2px 0 12px ${GM.amethyst}14`,
    }}>
      {children}
      {sign && <div style={{ marginTop: 10, textAlign: "right", color: JX, fontWeight: 900, letterSpacing: 2, fontSize: 12, textShadow: `0 0 10px ${JX}66` }}>— J.</div>}
    </div>
  );
}

// ── Inline helpers ───────────────────────────────────────────




function Eye({ children }) {
  return <div style={{ fontSize: 10, color: GM.inkGold, letterSpacing: 6, textAlign: "center", marginBottom: 18, textShadow: `0 0 8px ${GM.inkGold}44` }}>◆ {children} ◆</div>;
}

// ── Chapter nav config ────────────────────────────────────────
// ════════════════════════════════════════════════════════════════════
// 📖 THE CONTENTS, AND THE PAGES, ARE `RULEZ_V2.md` ITSELF
// ════════════════════════════════════════════════════════════════════
// caP'n, 2026-09-13, asked whether this file should be rewritten by hand
// to match the book or should RENDER it: **"16) hell yeah beautiful :3"**.
//
// ⚠ WHAT USED TO BE HERE: a hand-written `CHAPTERS` array of thirty rows
// and a `CHAPTER_BODY` map of thirty hand-written JSX chapters, ~150KB of
// prose paraphrasing a book that lives one directory up. The 2026-09-08
// coherency pass found **twenty-six places where the two disagreed** — a
// Curio that took "One Ally Ilk" where the book says two, an U'hlti
// attachable "from L1" where the book says L3, a Turn defined as half a
// Turn — plus **two whole chapters the book had and this file did not**.
// Every one of those was somebody faithfully copying a rule and the rule
// moving afterwards.
//
// **The only copy that cannot drift is the one that is not a copy.**
//
// ── HOW IT WORKS, AND WHAT IT COSTS ─────────────────────────────────
// `RulezRender.cut()` slices the book at its own `##` headings, so the
// contents list, the page turns, the progress dots and the search index
// are all DERIVED. A chapter added to `RULEZ_V2.md` tomorrow appears
// here with no edit to this file at all. The Part each chapter belongs
// to comes from the book's own `# PART` dividers.
//
// The cost is that the labels are the book's headings rather than the
// curated ones this file used to carry — and the emoji are kept, in
// CHAPTER_ICON below, because an icon is CHROME and not a rule. An id
// with no icon gets ◆ and nothing breaks.
//
// ⚠ TWO PAGES ARE STILL HAND-WRITTEN AND BOTH ARE CEREMONY, NOT RULES:
// `ChWarning` and `ChInvitation` carry the book's opening flow (the
// "enter" gate and the "next" hand-off) and animate. Their PROSE still
// comes from the markdown — they are wrappers, not second copies.
// Keys are chapter-title slugs from the CURRENT RULEZ_V2.md — a slug
// only matches the chapter whose exact heading text produces it (see
// `slug()` in RulezRender.js). Rewriting a chapter's heading wording
// changes its slug, so this map goes stale silently unless it is
// re-checked against the book after any heading rewrite. Refreshed
// 2026-09-22 against the pure-facts rewrite's actual chapter titles;
// an id with no match here just falls back to ◆, so a miss is cosmetic,
// never a crash.
const CHAPTER_ICON = {
  "a-warning": "⚠", "the-shatterfield": "🌀", "the-two-decks": "📚",
  "resources-anima-khao": "✦", "the-turn": "🔄", "the-measure-of-a-turn": "⏳",
  "the-axis-ilk-school": "🧭", "esclings-escendants": "👤", "combat": "💀",
  "souls-attachments": "✦", "impera-training": "⭐",
  "the-primera-innate-stamps": "◈", "the-role-rifts": "🜂", "tomes": "📖",
  "curios": "💎", "answering": "⚡", "u-hltis-affinities-supreme-beings": "◆",
  "resurrection": "✨", "prophecies-graveshift": "🔮", "victory-the-three-clocks": "🏆",
  "the-deviant-paths": "🔀", "heretical-training": "🔥",
  "exiliation-the-exile-rift": "🕳", "rulings-edge-cases": "⚖",
  "acronyms-shorthand": "🗂", "glossary": "📜", "quick-reference": "⚡",
};
const chapterLabel = (sec) => {
  const icon = CHAPTER_ICON[sec.id] || "◆";
  // "Chapter 7: Esclings & Escendants" reads as "Esclings & Escendants"
  // in a contents list that is already numbered by position.
  const bare = sec.title.replace(/^Chapter\s+\d+:\s*/, "").replace(/^PART\s+\S+\s*[—-]\s*/i, "");
  return `${icon} ${bare}`;
};

const CHAPTERS = RULEZ_SECTIONS.map((sec) => ({
  id: sec.id, label: chapterLabel(sec), part: sec.part,
}));

// ── ONE SOURCE OF TRUTH for chapter bodies ──────────────────
// The page render AND the search index both come through this map.
// Factories take { go } — the live reader passes the real navigator,
// the indexer passes a noop.
const CHAPTER_BODY = Object.fromEntries(RULEZ_SECTIONS.map((sec) => {
  if (sec.id === "a-warning") {
    return [sec.id, ({ go }) => <ChWarning onEnter={() => go(RULEZ_SECTIONS[1]?.id)} md={sec.body} />];
  }
  if (sec.id === "j-ynxi-s-invitation") {
    return [sec.id, ({ go }) => <ChInvitation onNext={() => go(RULEZ_SECTIONS[2]?.id)} md={sec.body} />];
  }
  return [sec.id, () => <RulezChapter id={sec.id} />];
}));

// ── THE SEARCH WELL NOW SEARCHES THE BOOK ───────────────────
// ⚠ IT USED TO SERVER-RENDER EVERY CHAPTER TO HTML AND STRIP THE TAGS.
// That was the only honest way to index thirty hand-written JSX pages,
// and it dragged `react-dom/server` into a screen for the sake of a
// substring match. Now the text IS text: `rulezText` returns the
// Markdown of a section with its punctuation stripped, so the index is
// built out of the same source the page is — and a chapter can never be
// readable but unsearchable, or searchable but out of date.
let RB_SEARCH_INDEX = null;
function buildSearchIndex() {
  if (RB_SEARCH_INDEX) return RB_SEARCH_INDEX;
  RB_SEARCH_INDEX = CHAPTERS.map((c) => ({ id: c.id, label: c.label, text: rulezText(c.id) }));
  return RB_SEARCH_INDEX;
}

// ── Page-turn footer — the book's spine ──────────────────────
function PageTurnBtn({ dir, chapter, onClick }) {
  const [hov, setHov] = useState(false);
  const isNext = dir === "next";
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        flex: 1, maxWidth: 240, padding: "12px 14px",
        display: "flex", flexDirection: "column", gap: 3,
        alignItems: isNext ? "flex-end" : "flex-start",
        textAlign: isNext ? "right" : "left",
        background: hov ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
        border: "none", clipPath: facetTB(9), cursor: "pointer",
        boxShadow: hov
          ? `inset 0 1px 0 ${GM.inkGoldBright}88, ${bloom(GM.inkGoldBright)}`
          : SHADOW_RAISED,
        filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.55))",
        transition: "all 0.18s ease",
        fontFamily: GM.font,
      }}>
      <span style={{ fontSize: 8, letterSpacing: 3, color: hov ? GM.inkGoldBright : GM.vellumFaint, fontWeight: 900, transition: "color 0.18s" }}>
        {isNext ? "TURN THE PAGE →" : "← PREVIOUS"}
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, color: hov ? GM.inkGoldBright : GM.vellumDim, letterSpacing: 0.5, textShadow: hov ? textGlow(GM.inkGoldBright) : TEXT_SET, transition: "all 0.18s" }}>
        {chapter.label}
      </span>
    </button>
  );
}

function PageTurn({ prev, next, go }) {
  if (!prev && !next) return null;
  return (
    <div style={{ maxWidth: 560, margin: "28px auto 0" }}>
      <OrnateDivider />
      <div style={{ display: "flex", gap: 10, justifyContent: "space-between", marginTop: 14 }}>
        {prev ? <PageTurnBtn dir="prev" chapter={prev} onClick={() => go(prev.id)} /> : <div style={{ flex: 1, maxWidth: 240 }} />}
        {next ? <PageTurnBtn dir="next" chapter={next} onClick={() => go(next.id)} /> : <div style={{ flex: 1, maxWidth: 240 }} />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
export default function Rulebook({ onBack }) {
  const stage = useStageRect(); // C5 — the one canonical room rectangle
  // The book remembers you — resume at the last chapter read
  const [ch, setCh] = useState(() => {
    // ⚠ THE FALLBACK IS THE BOOK'S FIRST SECTION, NOT A LITERAL "warning".
    // The ids are derived from `RULEZ_V2.md`'s own headings now, so a
    // hard-coded one is a chapter this file believes in and the book may
    // not have. A saved position from before the ids changed simply
    // fails the `some` test and lands the reader on page one.
    const FIRST = CHAPTERS[0]?.id;
    const { last } = loadRB();
    return last && CHAPTERS.some(c => c.id === last) ? last : FIRST;
  });
  const [seen, setSeen] = useState(() => {
    const stored = new Set(loadRB().seen);
    stored.add(loadRB().last || CHAPTERS[0]?.id);
    return stored;
  });
  const [partFilter, setPartFilter] = useState(null); // null = all
  const bodyRef = useRef();

  // ── T15 — THE SEARCH WELL (2026-08-18) ──────────────────────
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);
  useEffect(() => {
    if (searchOpen) setTimeout(() => searchInputRef.current?.focus(), 60);
  }, [searchOpen]);

  const go = useCallback((id) => {
    setCh(id);
    setSeen(prev => {
      const nextSeen = new Set(prev);
      nextSeen.add(id);
      saveRB(id, nextSeen);
      return nextSeen;
    });
    bodyRef.current?.scrollTo(0, 0);
  }, []);

  // Esc closes the book. Enter does NOT — you're reading. (v4 change)
  useModalKeys(onBack, { onEnter: () => {} });

  // ── T15 — search results, computed live over the lazy index ──
  const results = useMemo(() => {
    // ⚠ THE NEEDLE GOES THROUGH THE SAME DOOR AS THE HAYSTACK (2026-09-14).
    // This was `query.trim().toLowerCase()` while the index was built by
    // `rulezText`, which strips `-` to a space — so a search for
    // "forty-five seconds", "half-turn" or "face-down" could never match
    // text that is printed in the book. Found by typing it into the well.
    const q = rulezNormalize(query).toLowerCase();
    if (q.length < 2) return null;
    const idx = buildSearchIndex();
    const out = [];
    for (const e of idx) {
      const hay = e.text.toLowerCase();
      const first = hay.indexOf(q);
      if (first === -1) continue;
      let count = 0, pos = first;
      while (pos !== -1 && count < 99) { count++; pos = hay.indexOf(q, pos + q.length); }
      const start = Math.max(0, first - 55);
      const lead = start > 0 ? "…" : "";
      const raw = e.text.slice(start, first + q.length + 95);
      out.push({
        id: e.id, label: e.label, count,
        pre: lead + raw.slice(0, first - start),
        hit: raw.slice(first - start, first - start + q.length),
        post: raw.slice(first - start + q.length) + "…",
      });
    }
    return out.sort((a, b) => b.count - a.count);
  }, [query]);

  const closeSearch = useCallback(() => { setSearchOpen(false); setQuery(""); }, []);
  const goFromSearch = useCallback((id) => {
    setPartFilter(null); // the well searches the whole book — never land hidden
    go(id);
    closeSearch();
  }, [go, closeSearch]);
  // While the well is open it is the top modal layer: Escape closes
  // the WELL (not the book — the stack law), Enter jumps to the top hit.
  useModalKeys(closeSearch, {
    enabled: searchOpen,
    onEnter: () => { if (results?.length) goFromSearch(results[0].id); },
  });

  const visibleChapters = partFilter === null ? CHAPTERS : CHAPTERS.filter(c => c.part === partFilter || c.part === 0);
  const visIdx = visibleChapters.findIndex(c => c.id === ch);
  const prevCh = visIdx > 0 ? visibleChapters[visIdx - 1] : null;
  const nextCh = visIdx >= 0 && visIdx < visibleChapters.length - 1 ? visibleChapters[visIdx + 1] : null;

  // If the filter hides the current chapter, jump to the first chapter of that part
  useEffect(() => {
    if (partFilter !== null && !visibleChapters.some(c => c.id === ch)) {
      const first = CHAPTERS.find(c => c.part === partFilter);
      if (first) go(first.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partFilter]);

  // ←/→ turn the page (ignored while typing in the glossary search)
  useEffect(() => {
    const handler = (e) => {
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      if (e.key === "ArrowRight" && nextCh) { e.preventDefault(); go(nextCh.id); }
      if (e.key === "ArrowLeft" && prevCh)  { e.preventDefault(); go(prevCh.id); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [prevCh, nextCh, go]);

  // Keep the active chapter chip in view
  useEffect(() => {
    const el = document.getElementById(`rb-chip-${ch}`);
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [ch]);

  // Reading progress across the whole book
  const bookIdx = CHAPTERS.findIndex(c => c.id === ch);
  const progressPct = Math.round(((bookIdx + 1) / CHAPTERS.length) * 100);

  return (
    <div style={{ position: "fixed", inset: 0, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", fontFamily: GM.font }}>
    {/* C5 backdrop wave (2026-08-18): the book lives inside the ONE
        canonical room rectangle; root is transparent so the equipped
        backdrop letterboxes around it (the Emporium pattern). */}
    <div style={{ position: "relative", width: stage.w, height: stage.h, flexShrink: 0, display: "flex", flexDirection: "column", background: GM.page, backgroundImage: GM.candlelight, overflow: "hidden" }}>
      <UnstableWorldBG />

      {/* ── Header — forged metal frame ── */}
      <div style={{ position: "relative", zIndex: 10, flexShrink: 0, background: `linear-gradient(175deg, #1a1230 0%, ${GM.pageMid} 55%, #0c0818 100%)`, boxShadow: `inset 0 1px 0 rgba(245,216,120,0.22), 0 4px 14px rgba(0,0,0,0.6)`, backdropFilter: "blur(10px)", padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: GOLD_METAL_H, opacity: 0.9 }} />
        <button onClick={onBack} style={{ background: OBSIDIAN_SLAB, border: "none", clipPath: facetTB(7), color: GM.vellumFaint, padding: "8px 14px", cursor: "pointer", fontFamily: GM.font, fontSize: 10, letterSpacing: 1, fontWeight: 700, boxShadow: SHADOW_RAISED, filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.5))" }}>‹ BACK</button>
        <div style={{ flex: 1, textAlign: "center" }}>
          <div style={{ fontSize: 8, color: GM.inkGold, letterSpacing: 8, marginBottom: 2, textShadow: `0 0 8px ${GM.inkGold}44` }}>◆ THE GRIMOIRE OF ◆</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: GM.inkGoldBright, fontFamily: GM.fontD, letterSpacing: 6, textShadow: textGlow(GM.inkGoldBright) }}>TERROR</div>
          <div style={{ fontSize: 8, color: GM.vellumFaint, letterSpacing: 4 }}>COMPLETE RULEBOOK · v4 · RULEZ V2 CANON</div>
        </div>
        {/* T15 — the well's mouth */}
        <button
          onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
          title="Search the book"
          style={{
            background: searchOpen ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB, border: "none", clipPath: facetTB(7),
            color: searchOpen ? GM.inkGoldBright : GM.vellumFaint, padding: "8px 12px", cursor: "pointer",
            fontFamily: GM.font, fontSize: 12, fontWeight: 700,
            boxShadow: searchOpen ? `inset 0 1px 0 ${GM.inkGoldBright}88, ${bloom(GM.inkGoldBright)}` : SHADOW_RAISED,
            filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.5))", transition: "all 0.15s",
          }}>🔍</button>
        <div style={{ width: 68, display: "flex", justifyContent: "flex-end" }}>
          <span style={{ fontSize: 8, color: GM.vellumFaint, letterSpacing: 1, fontFamily: GM.fontUI, textAlign: "right", lineHeight: 1.6 }}>
            FOLIO<br />
            <span style={{ color: GM.inkGoldBright, fontSize: 11, fontWeight: 700, textShadow: TEXT_SET }}>{bookIdx + 1} / {CHAPTERS.length}</span>
          </span>
        </div>
      </div>

      {/* ── T15 — THE SEARCH WELL — a shaft sunk through every page ── */}
      {searchOpen && (
        <div style={{ position: "absolute", top: 64, left: 0, right: 0, zIndex: 30, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
          <div style={{
            pointerEvents: "auto", width: "min(560px, 94%)", maxHeight: "70%",
            display: "flex", flexDirection: "column",
            background: `linear-gradient(175deg, #1a1230f6 0%, ${GM.pageMid}f6 55%, #0c0818f8 100%)`,
            boxShadow: `inset 0 1px 0 ${GM.inkGoldBright}55, 0 18px 48px rgba(0,0,0,0.75)`,
            clipPath: facetTB(12), backdropFilter: "blur(10px)", padding: 12,
          }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 13 }}>🔍</span>
              <input
                ref={searchInputRef}
                value={query}
                onChange={(e) => { buildSearchIndex(); setQuery(e.target.value); }}
                placeholder="Search the whole book…"
                style={{
                  flex: 1, background: OBSIDIAN_RECESS, border: "none", outline: "none",
                  boxShadow: SHADOW_RECESS, clipPath: facetTB(7), padding: "10px 12px",
                  color: GM.inkGoldBright, fontFamily: GM.fontBody || GM.font, fontSize: 13,
                  letterSpacing: 0.4, caretColor: GM.inkGoldBright,
                }}
              />
              <button aria-label="Close" onClick={closeSearch} style={{ background: OBSIDIAN_SLAB, border: "none", clipPath: facetTB(6), color: GM.vellumFaint, padding: "8px 11px", cursor: "pointer", fontFamily: GM.font, fontSize: 10, fontWeight: 900, boxShadow: SHADOW_RAISED }}>✕</button>
            </div>
            {results !== null && (
              <div style={{ marginTop: 10, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                {results.length === 0 && (
                  <div style={{ padding: "16px 8px", textAlign: "center", color: GM.vellumFaint, fontSize: 11, fontStyle: "italic" }}>
                    The well comes up dry — no page holds “{query.trim()}”.
                  </div>
                )}
                {results.map((r, i) => (
                  <button key={r.id} onClick={() => goFromSearch(r.id)} style={{
                    textAlign: "left", background: i === 0 ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB, border: "none",
                    clipPath: facetTB(8), padding: "10px 12px", cursor: "pointer", fontFamily: GM.font,
                    boxShadow: i === 0 ? `inset 0 1px 0 ${GM.inkGoldBright}66` : SHADOW_RAISED, transition: "all 0.12s",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ color: GM.inkGoldBright, fontSize: 11, fontWeight: 900, letterSpacing: 0.5, textShadow: TEXT_SET }}>{r.label}</span>
                      <span style={{ color: GM.vellumFaint, fontSize: 9, fontWeight: 700, letterSpacing: 1, flexShrink: 0 }}>{r.count} {r.count === 1 ? "MATCH" : "MATCHES"}</span>
                    </div>
                    <div style={{ color: GM.vellumDim, fontSize: 10.5, lineHeight: 1.55, fontFamily: GM.fontBody || GM.font }}>
                      {r.pre}<span style={{ color: GM.inkGoldBright, background: `${GM.inkGold}2e`, fontWeight: 700 }}>{r.hit}</span>{r.post}
                    </div>
                  </button>
                ))}
                {results.length > 0 && (
                  <div style={{ textAlign: "center", color: GM.vellumFaint, fontSize: 8.5, letterSpacing: 1.5, padding: "4px 0 2px" }}>
                    ENTER — TOP RESULT · ESC — CLOSE THE WELL
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Reading progress — gold vein filling the spine ── */}
      <div style={{ position: "relative", zIndex: 10, flexShrink: 0, height: 3, background: OBSIDIAN_RECESS, boxShadow: SHADOW_RECESS }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${progressPct}%`, background: GOLD_METAL_H, boxShadow: `0 0 8px ${GM.inkGoldBright}66`, transition: "width 0.45s ease" }} />
      </div>

      {/* ── Part filter — faceted tabs ── */}
      <div style={{ position: "relative", zIndex: 10, flexShrink: 0, display: "flex", gap: 5, padding: "7px 10px", background: `${GM.pageWarm}e8`, borderBottom: `1px solid ${GM.pageBorder}`, backdropFilter: "blur(6px)" }}>
        {[
          [null, "ALL"],
          [1, "I · SETUP"],
          [2, "II · SYSTEMS"],
          [3, "III · REFERENCE"],
        ].map(([pf, label]) => {
          const active = partFilter === pf;
          return (
            <button key={label} onClick={() => setPartFilter(pf)} style={{
              flex: 1, padding: "6px 6px", fontSize: 9, fontWeight: 900, letterSpacing: 1,
              background: active ? OBSIDIAN_SLAB_LIT : "transparent",
              border: "none", clipPath: facetTB(6),
              boxShadow: active ? `inset 0 1px 0 ${GM.inkGoldBright}88, inset 0 0 16px ${GM.amethyst}30` : `inset 0 0 0 1px ${GM.pageBorder}`,
              color: active ? GM.inkGoldBright : GM.vellumFaint,
              textShadow: active ? textGlow(GM.inkGoldBright) : "none",
              cursor: "pointer", fontFamily: GM.font, transition: "all 0.15s",
            }}>{label}</button>
          );
        })}
      </div>

      {/* ── Chapter nav — faceted chips with visited gems ── */}
      <div style={{ position: "relative", zIndex: 10, flexShrink: 0, display: "flex", overflowX: "auto", gap: 5, padding: "8px 10px", background: `${GM.pageWarm}f0`, borderBottom: `1px solid ${GM.pageBorder}`, backdropFilter: "blur(6px)" }}>
        {visibleChapters.map(c => {
          const active = ch === c.id;
          const visited = seen.has(c.id);
          return (
            <button key={c.id} id={`rb-chip-${c.id}`} onClick={() => go(c.id)} style={{
              flexShrink: 0, padding: "6px 12px", display: "flex", alignItems: "center", gap: 6,
              background: active ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB,
              border: "none", clipPath: facetTB(7),
              boxShadow: active
                ? `inset 0 1px 0 ${GM.inkGoldBright}99, inset 0 0 18px ${GM.amethyst}35`
                : SHADOW_RAISED,
              cursor: "pointer",
              color: active ? GM.inkGoldBright : GM.vellumFaint,
              textShadow: active ? textGlow(GM.inkGoldBright) : TEXT_SET,
              fontFamily: GM.font, fontSize: 9, fontWeight: 700, letterSpacing: 0.5, whiteSpace: "nowrap", transition: "all 0.15s",
            }}>
              {visited && !active && <span style={{ width: 4, height: 4, borderRadius: "50%", background: GM.inkGold, boxShadow: `0 0 5px ${GM.inkGold}`, flexShrink: 0 }} />}
              {c.label}
            </button>
          );
        })}
      </div>

      {/* ── Content — the page ── */}
      <div ref={bodyRef} style={{ position: "relative", zIndex: 2, flex: 1, overflowY: "auto", padding: "20px 14px 20px" }}>
        <div key={ch} style={{ animation: "rbPageIn 0.35s ease" }}>
          {/* T15 — pages come through CHAPTER_BODY, the same map the
              search well indexes. One source, no drift. */}
          {CHAPTER_BODY[ch]?.({ go }) ?? null}
          <PageTurn prev={prevCh} next={nextCh} go={go} />
        </div>
      </div>

      {/* ── Hint bar — matches the Help menu's voice ── */}
      <div style={{ position: "relative", zIndex: 10, flexShrink: 0, textAlign: "center", padding: "7px 10px", background: `${GM.pageWarm}f0`, borderTop: `1px solid ${GM.pageBorder}`, backdropFilter: "blur(6px)" }}>
        <div style={{ position: "absolute", top: 0, left: "15%", right: "15%", height: 1, background: SILVER_METAL_H, opacity: 0.2 }} />
        <span style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 2, fontFamily: GM.font }}>← → TURN PAGE · ESC BACK</span>
      </div>
    </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// FRONT MATTER
// ═══════════════════════════════════════════════════════════
// ⚠ THE PROSE IS THE BOOK'S, THE CEREMONY IS THIS FILE'S. Both of these
// used to carry their paragraphs inline — a second copy of the two pages
// a reader meets FIRST, which is the worst possible place for a
// disagreement. `md` is the section straight out of `RULEZ_V2.md`; what
// stays here is the gate, the button and the animation.
function ChWarning({ onEnter, md }) {
  const [accepted, setAccepted] = useState(false);
  return (
    <div style={{ maxWidth: 520, margin: "0 auto", paddingTop: 20 }}>
      {/* ── THE TITLE PAGE (2026-09-14) ─────────────────────────────
          `RULEZ_PREAMBLE` is everything in RULEZ_V2.md above its first
          `##` — the title, the byline and the EDITION LINE. It was
          exported with ZERO call sites, so the one part of the book that
          says WHICH book it is never reached a reader, and playerText's
          "not one line is lost" check counted those lines as safe because
          they were in a variable rather than because they were on screen.
          A physical book opens on its title page; so does this one. */}
      <div style={{ textAlign: "center", color: GM.vellumDim, marginBottom: 26, opacity: 0.92 }}>
        <RulezMarkdown md={RULEZ_PREAMBLE} />
      </div>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ fontSize: 44, marginBottom: 8, filter: "drop-shadow(0 0 12px rgba(220,38,38,0.5))" }}>☠</div>
        <div style={{ fontSize: 9, color: GM.inkRed, letterSpacing: 8, marginBottom: 8 }}>A WARNING</div>
        <div style={{ fontSize: 26, fontWeight: 900, color: "#f87171", fontFamily: GM.fontD, letterSpacing: 3, textShadow: "0 0 22px #dc262688, 0 1px 2px #000" }}>READ THIS FIRST</div>
      </div>
      <div style={{ background: `linear-gradient(145deg,#dc262612,${GM.pageWarm})`, border: "1px solid #dc262655", borderRadius: 12, padding: "22px 24px", marginBottom: 18, lineHeight: 2.1, color: GM.vellumDim, fontSize: 15, fontStyle: "italic", fontFamily: GM.fontBody, boxShadow: "inset 0 1px 0 rgba(248,113,113,0.15), 0 8px 24px rgba(0,0,0,0.5)" }}>
        <RulezMarkdown md={md} />
      </div>
      <button onClick={() => setAccepted(true)} style={{ width: "100%", padding: "13px", background: accepted ? "#dc262622" : OBSIDIAN_SLAB, border: "none", clipPath: facetTB(9), color: accepted ? GM.vellumFaint : "#f87171", cursor: "pointer", fontFamily: GM.font, fontSize: 11, fontWeight: 900, letterSpacing: 2, marginBottom: 10, transition: "all 0.3s", boxShadow: "inset 0 1px 0 rgba(224,85,85,0.4), inset 0 -6px 14px rgba(0,0,0,0.45)", filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.6))", textShadow: TEXT_SET }}>
        {accepted ? "✓ I ACCEPT THE RISK" : "I UNDERSTAND — PLAY AT MY OWN RISK"}
      </button>
      {accepted && (
        <button onClick={onEnter} style={{ width: "100%", padding: "13px", background: OBSIDIAN_SLAB_LIT, border: "none", clipPath: facetTB(9), color: GM.inkGoldBright, cursor: "pointer", fontFamily: GM.font, fontSize: 11, fontWeight: 900, letterSpacing: 2, boxShadow: `inset 0 1px 0 #f5d87888, ${bloom(GM.inkGoldBright)}`, filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.6))", transition: "all 0.3s", textShadow: textGlow(GM.inkGoldBright), animation: "rbPageIn 0.3s ease" }}>
          ENTER THE GRIMOIRE →
        </button>
      )}
    </div>
  );
}

function ChInvitation({ onNext, md }) {
  return (
    <div style={{ maxWidth: 520, margin: "0 auto" }}>
      <Eye>J'YNXI'S INVITATION</Eye>
      <OrnateDivider color={JX} />
      <Jynxi><RulezMarkdown md={md} /></Jynxi>
      <div style={{ textAlign: "right", color: JX, fontWeight: 900, fontSize: 14, fontFamily: GM.fontD, letterSpacing: 2, marginTop: -8, marginRight: 4, textShadow: `0 0 10px ${JX}66` }}>— J.</div>
      <button onClick={onNext} style={{ width: "100%", marginTop: 20, padding: "13px", background: OBSIDIAN_SLAB_LIT, border: "none", clipPath: facetTB(9), color: GM.inkGoldBright, cursor: "pointer", fontFamily: GM.font, fontSize: 11, fontWeight: 900, letterSpacing: 2, boxShadow: `inset 0 1px 0 #f5d87877, inset 0 -6px 14px rgba(0,0,0,0.4)`, filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.6))", textShadow: TEXT_SET }}>
        BEGIN →
      </button>
    </div>
  );
}

// ── AND THE FOURTEEN TYPESETTING HELPERS WENT WITH THEM ─────────────
// T · Prose · S · Tip · Warn · Rule · New · Box · StatRow · Grid2 ·
// CostTable · H2 · PartHeader — a small house style for hand-written
// chapters, with no hand-written chapters left to set. `RulezRender`
// does the typesetting now, from the book's own Markdown, which is the
// only way the type can never say something the text does not.
//
// They are DELETED rather than shielded, for the same reason the
// chapters were: a helper that formats a rulebook is part of the copy.

// ════════════════════════════════════════════════════════════════════
// ⚠ TWENTY-SEVEN HAND-WRITTEN CHAPTERS WERE DELETED HERE ON 2026-09-13
// ════════════════════════════════════════════════════════════════════
// `ChHowToRead` through `ChFarewell` — roughly 150KB of JSX that
// paraphrased `RULEZ_V2.md` chapter by chapter. They are not "retired
// code that gets a shield": a shielded copy of a rulebook is still a
// copy of a rulebook, and a copy is exactly what caP'n's answer to
// question 16 was about.
//
// THE PAGES COME FROM THE BOOK NOW. See the banner above CHAPTER_ICON.
//
// What this file still owns, and rightly: the reading CHROME — the
// spine, the page turns, the contents rail, the progress dots, the
// search well, the ceremony of the Warning and the Invitation. Those
// are how the book is READ. None of them is a rule.
//
// ⚠ IF A PAGE IS WRONG, FIX `RULEZ_V2.md`. That sentence is the whole
// ruling, and it is the last line of defence against the twenty-six
// disagreements the 2026-09-08 pass found between this file and the
// book — every one of which was somebody copying a rule faithfully and
// the rule moving afterwards.

